import { Checkbox } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import React, { type CSSProperties, useContext, useEffect, useMemo } from "react";
import type { Column } from "./Column";
import { SortButton } from "./SortButton";
import { TableController, TableRecord } from "./TableController";
import { TableFooter } from "./TableFooter";
import { TableHeader as ExternalTableHeader } from "./TableHeader";
import { TableRow } from "./TableRow";

interface TableProps<Item extends TableRecord> {
  columns: Column<Item>[];
  className?: string;
  selectable?: boolean;
  /**
   * Renders inside the framed table header (first row inside the border). Prefer `toolbar`
   * for filters; use this only when controls must sit with the column header chrome.
   */
  actionRow?: () => React.ReactNode;
  /**
   * Renders above the framed table (outside the border). **Put table filters here** (and
   * short hints), e.g. selects and scope toggles. Keep heavy primary actions on the `Panel` or
   * page header when possible.
   */
  toolbar?: () => React.ReactNode;
  /** When false, the sort-button row under headers is hidden (sorting still applies). Default true. */
  showSortRow?: boolean;
  /** When false, no outer rounded border. Default true. */
  framed?: boolean;
  /** Renders inside tbody when items exist but filters hide every row. */
  emptyFilteredContent?: () => React.ReactNode;
}

const TableContext = React.createContext<TableProps<any>>({ columns: [], showSortRow: true });

const ColGroup = ApplicationView(
  ({ columns, selectable }: { columns: Column<any>[]; selectable?: boolean }) => {
    return (
      <colgroup>
        {selectable && <col style={{ width: "30px" }} />}
        {columns.map((column, index) => {
          const style: CSSProperties = {
            width: column.colWidth ? `${column.colWidth}%` : "auto",
            visibility: column.visible ? (column.visible() ? "initial" : "collapse") : "initial",
          };

          return <col key={index} style={style} />;
        })}
      </colgroup>
    );
  },
);

const TableCell = ApplicationView(({ column, item }: { column: Column<any>; item: TableRecord }) => {
  return <td className={column.className}>{column.cell(item)}</td>;
});

const TableItemRow = ApplicationView(
  ({ selectable, columns, item }: { selectable?: boolean; columns: Column<any>[]; item: TableRecord }) => {
    const controller = TableController.use() as unknown as TableController<TableRecord>;
    const selected = controller.isSelected(item);

    return (
      <TableRow className="*:text-sm *:py-3 *:px-3" selected={selected}>
        {selectable && (
          <td>
            <input
              type="checkbox"
              checked={selected}
              onChange={(event) => {
                if (event.target.checked) {
                  controller.actionSelectItem(item);
                } else {
                  controller.actionDeselectItem(item);
                }
              }}
            />
          </td>
        )}
        {columns.map((column, colIndex) => (
          <TableCell key={colIndex} column={column} item={item} />
        ))}
      </TableRow>
    );
  },
);

const SelectableHeader = ApplicationView(() => {
  const controller = TableController.use() as unknown as TableController<TableRecord>;

  return (
    <th className="px-3 py-3">
      <Checkbox
        checked={controller.allSelected}
        indeterminate={!controller.allSelected && controller.anySelected}
        className={clsx("h-4 w-4 rounded border-gray-300 focus:ring-indigo-500", {
          "text-indigo-600 checked:bg-indigo-600": controller.allSelected || !controller.anySelected,
          "bg-gray-600 text-gray-600 hover:bg-gray-700": !controller.allSelected && controller.anySelected,
        })}
        onChange={(event) => controller.actionSelectAll(event.target.checked)}
      />
    </th>
  );
});

const TableHeader = ApplicationView(() => {
  const { columns, selectable, actionRow, showSortRow = true } = useContext(TableContext);
  const controller = TableController.use() as unknown as TableController<TableRecord>;
  const sortable = columns.some((column) => column.sortable);

  return (
    <ExternalTableHeader>
      {actionRow && (
        <tr>
          <td colSpan={columns.length + (selectable ? 1 : 0)} className="bg-white py-2 text-sm">
            {actionRow()}
          </td>
        </tr>
      )}
      <tr className="bg-slate-100/85 *:whitespace-nowrap">
        {selectable && <SelectableHeader />}
        {columns.map((column, index) => (
          <th
            key={index}
            className={clsx(
              "text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600",
              column.headerClassName,
            )}
          >
            {column.header && <div className="border-gray-200">{column.header()}</div>}
          </th>
        ))}
      </tr>
      {sortable && showSortRow && (
        <tr className="bg-slate-100/70">
          {selectable && <th className="!px-2 !py-1.5" />}
          {columns.map((column, index) => (
            <th key={index} className={clsx("!px-2 !py-1.5", column.headerClassName)}>
              {column.sortable && (
                <SortButton
                  field={column.id}
                  currentField={controller.state.sortField}
                  direction={controller.state.sortDirection}
                  onSort={(id) => controller.actionSort(id)}
                />
              )}
            </th>
          ))}
        </tr>
      )}
    </ExternalTableHeader>
  );
});

function UncontrolledTable<Item extends TableRecord>({
  columns,
  className,
  selectable,
  actionRow,
  toolbar,
  showSortRow = true,
  framed = true,
  emptyFilteredContent,
}: TableProps<Item>) {
  const controller = TableController.use() as unknown as TableController<Item>;
  const visibleItems = controller.visibleItems;
  const showEmptyFiltered =
    emptyFilteredContent != null &&
    controller.state.items.length > 0 &&
    controller.filteredItems.length === 0;
  const context = useMemo(
    () => ({ columns, selectable, actionRow, showSortRow }),
    [columns, selectable, actionRow, showSortRow],
  );

  useEffect(() => {
    controller.updateColumns(columns);
  }, [columns]);

  const tableElement = (
    <table className={clsx("relative w-full border-separate border-spacing-0", className)}>
      <ColGroup selectable={selectable} columns={columns} />
      <TableHeader />
      <tbody>
        {showEmptyFiltered ? (
          <tr>
            <td colSpan={columns.length + (selectable ? 1 : 0)} className="bg-white">
              {emptyFilteredContent()}
            </td>
          </tr>
        ) : (
          visibleItems.map((item, index) => (
            <TableItemRow key={index} columns={columns} item={item} selectable={selectable} />
          ))
        )}
      </tbody>
      <TableFooter colSpan={columns.length + (selectable ? 1 : 0)} />
    </table>
  );

  const framedTable = framed ? (
    <div className="overflow-clip rounded-lg border border-slate-300">{tableElement}</div>
  ) : (
    tableElement
  );

  return (
    <TableContext.Provider value={context}>
      {toolbar ? (
        <div className="flex flex-col gap-2">
          <div className="w-full">{toolbar()}</div>
          {framedTable}
        </div>
      ) : (
        framedTable
      )}
    </TableContext.Provider>
  );
}

const ControlledTable = ApplicationView(UncontrolledTable);

export function Table<Item extends TableRecord>(props: TableProps<Item>) {
  return <ControlledTable {...props} />;
}
